import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { getUnixTime, isPast } from 'date-fns';

import { EnvironmentConfiguration, UserConfiguration } from '@rx-applications/clientsite/shared/config/utils';
import { AmplitudeService, DatePickerChanges } from '@rx-applications/shared/utils';
import { LogChannel } from '@rx-applications/shared/logging';

import { LocationSearchRequestLogging } from '../../models/amplitude-properties.model';
import { LoggingService } from '../../services/logging.service';
import { UserProperties } from '../../models/intercom-message.model';
import {
  AutocompleteLocationResponse,
  HotelPricingSummary,
  HotelSearchResponse,
  PropertyTypeName,
  RoomPriceOption,
  SearchForm,
} from '@rx-applications/clientsite/shared/utils';
import { AmplitudeHandler, DatadogHandler, IntercomHandler } from '../../handlers';
import { ClientsiteSharedLoggingService } from '../../services';
import { EventName, LogChannelMappings } from '../../constants/logging-data';

export enum UserProperty {
  IsImpersonated = 'IsImpersonated',
  Username = 'Username',
  Company = 'Company',
  WebsiteId = 'WebsiteId',
  Version = 'Version',
  Reports = 'Reports',
  AbTest = 'AbTest',
}

@Injectable({
  providedIn: 'root',
})
export class LoggingFacade {
  constructor(
    private amplitudeHandler: AmplitudeHandler,
    //SharedAmplitudeService is initiated for the nx libs that are part of the clientsite application
    private sharedAmplitudeService: AmplitudeService,
    private datadogHandler: DatadogHandler,
    private intercomHandler: IntercomHandler,
    private loggingService: LoggingService,
    private clientsiteSharedLoggingService: ClientsiteSharedLoggingService,
  ) {}

  setUserProperties(properties: UserProperties) {
    this.intercomHandler.update(properties);
  }

  resetAmplitudeSession() {
    this.amplitudeHandler.resetSessionId();
  }

  fullConfigLoaded(props: { time: number }) {
    this.trackConfigLoadTime(props.time * 1000000);
  }

  trackConfigLoadTime(time: number) {
    this.datadogHandler.configLoadTime = time;
  }

  logEventToDatadog(name: string, props = {}) {
    this.datadogHandler.logCustomEvent(name, props);
  }

  logErrorToDatadog(error: string | Error, context = {}) {
    this.datadogHandler.logCustomError(error, context);
  }

  didSearch(searchBoxValues: Params) {
    this.sendToPubSub(EventName.DidSearch, this.loggingService.didSearchParamMapping(searchBoxValues));
  }

  logAlert(type: string = 'danger', message: string, title: string): void {
    const props = { type, message, title };
    const capitalizedType = type[0].toUpperCase() + type.slice(1);
    const eventName = `${capitalizedType} Alert`;
    this.datadogHandler.logCustomEvent(eventName, props);
  }

  landedOnLoginScreen(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.LandedOnLoginScreen, props);
  }

  landedOnReports() {
    this.sendToPubSub(EventName.LandedOnReports);
  }

  reportDashboard() {
    this.sendToPubSub(EventName.ReportDashboard);
  }

  openRoomexPaySplash() {
    this.sendToPubSub(EventName.RoomexPaySplash);
  }

  landedOnCustomerDetailsPage(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.LandedOnCustomerDetailsPage, props);
  }

  clickedCustomerDetailsErrorLink() {
    this.sendToPubSub(EventName.ClickedCustomerDetailsErrorLink);
  }

  landedOnLandingPage() {
    this.sendToPubSub(EventName.LandedOnLandingPage);
  }

  landedGroupBookingPopOut(props: { path: 'EighthRoomSelect' | 'RoomSelectLink' }) {
    this.sendToPubSub(EventName.LandedGroupBookingPopOut, props);
  }

  groupBookingPopOutSent() {
    this.sendToPubSub(EventName.GroupBookingPopOutSent);
  }

  landedGroupBookingHelp() {
    this.sendToPubSub(EventName.LandedGroupBookingHelp);
  }

  groupBookingHelpSent() {
    this.sendToPubSub(EventName.GroupBookingHelpSent);
  }

  landedFAQHelpPage() {
    this.sendToPubSub(EventName.LandedFAQHelpPage);
  }

  landedContactHelpPage() {
    this.sendToPubSub(EventName.LandedContactHelpPage);
  }

  bookingEnquirySent() {
    this.sendToPubSub(EventName.BookingEnquirySent);
  }

  generalFeedbackSent() {
    this.sendToPubSub(EventName.GeneralFeedbackSent);
  }

  landedlCancellationHelpPage() {
    this.sendToPubSub(EventName.LandedlCancellationHelpPage);
  }

  selectedTopLocation(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.SelectedTopLocation, props);
  }

  showMapForHotel() {
    this.sendToPubSub(EventName.ShowMapForHotel);
  }

  showMapForLocation() {
    this.sendToPubSub(EventName.ShowMapForLocation);
  }

  landedOnLocationSearchPage(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.LandedOnLocationSearchPage, props);
  }

  termsAndConditionsClicked(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.TermsAndConditionsClicked, props);
  }

  selectedHotel(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.SelectedHotel, props);
  }

  clickedRoomDescription() {
    this.sendToPubSub(EventName.ClickedRoomDescription);
  }

  selectedRecentHotel(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.SelectedRecentHotel, props);
  }

  approvalFlowBookingStatus(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.ApprovalFlowBookingStatus, props);
  }

  locationSearchResponse(props: {
    searchQuery: LocationSearchRequestLogging;
    recommended: HotelPricingSummary[];
    nearby: HotelPricingSummary[];
    other: HotelPricingSummary[];
    timeTaken: number;
    timeToFirstInteraction: number;
    locationTooLargeUrl: string;
    numberOfPagesAtFirstInteraction: number;
    currentPage: number;
    hotelsToBeExcludedFromLocationSearch: number;
  }) {
    if (props.recommended.length || props.other.length || props.nearby.length) {
      this.sendToPubSub(EventName.LocationSearchResponse, {
        ...this.loggingService.locationSearchResponseParamMapping(
          props.searchQuery,
          props.recommended,
          props.other,
          props.nearby,
        ),
        TimeTaken: props.timeTaken,
        TimeToInteraction: props.timeToFirstInteraction,
        LocationTooLargeUrl: props.locationTooLargeUrl === 'true',
        NumberOfPagesAtFirstInteraction: props.numberOfPagesAtFirstInteraction,
        CurrentPage: props.currentPage,
        HotelsToBeExcludedFromLocationSearch: props.hotelsToBeExcludedFromLocationSearch,
      });
    }
  }

  changeSort(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.ChangeSort, props);
  }

  changedFilter(props: { [key: string]: string | number | boolean }) {
    const isApartment = props['propertyType'] === PropertyTypeName.Apartment;
    const isAparthotel = props['propertyType'] === PropertyTypeName.Aparthotel;
    const hasRestaurant = props['amenity'] === 'restaurant';

    if (isApartment || isAparthotel || hasRestaurant) {
      const eventName = `${EventName.ChangedFilter}:${props['propertyType'] || props['amenity']}`;
      this.clientsiteSharedLoggingService.sendToPubSub([LogChannel.Intercom], { name: eventName, props, type: null });
      this.clientsiteSharedLoggingService.sendToPubSub([LogChannel.Amplitude], {
        name: EventName.ChangedFilter,
        props,
        type: null,
      });
    } else {
      this.sendToPubSub(EventName.ChangedFilter, props);
    }
  }

  changedPriceRangeFilter(props: { endpoint: string }): void {
    this.sendToPubSub(EventName.ChangedPriceRangeFilter, props);
  }

  priceRangeFilterInteractions(props): void {
    this.sendToPubSub(EventName.PriceRangeFilterInteractions, { ...props });
  }

  changedStarRatingFilter(props: { stars: number[] }): void {
    this.sendToPubSub(EventName.ChangedStarRatingFilter, props);
  }

  reservationCreateResponse(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.ReservationCreateResponse, props);
  }

  finalizedBooking(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.FinalizedBooking, props);
  }

  sentSpecialRequest() {
    this.sendToPubSub(EventName.SentSpecialRequest);
  }

  expandedSearchRadius(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.ExpandedSearchRadius, props);
  }

  landedOnRoomSelectPage(props: {
    startApiTime: number;
    nights: any;
    results: HotelSearchResponse;
    selectedHotel: HotelPricingSummary;
  }) {
    this.sendToPubSub(
      EventName.LandedOnRoomSelectPage,
      this.loggingService.landedOnRoomSelectParamMapping(
        props.results,
        props.nights,
        props.startApiTime,
        props.selectedHotel,
      ),
    );
  }

  roomOptionClickResponse(props: {
    roomData: RoomPriceOption;
    inTerms: boolean;
    timestamp: number;
    rankSelected: number;
    totalRoomOptions: number;
  }) {
    this.sendToPubSub(
      EventName.RoomOptionClickResponse,
      this.loggingService.roomOptionClickParamMapping(
        props.roomData,
        props.inTerms,
        props.timestamp,
        props.rankSelected,
        props.totalRoomOptions,
      ),
    );
  }

  selectedRooms(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.SelectedRooms, props);
  }

  amendmentButtonClicked() {
    this.sendToPubSub(EventName.AmendmentButtonClicked);
  }

  amendGuest() {
    this.sendToPubSub(EventName.AmendGuestClicked);
  }

  submitGuestNameChange() {
    this.sendToPubSub(EventName.SubmitGuestNameChangeClicked);
  }

  guestNameAutoChanged() {
    this.sendToPubSub(EventName.GuestNameAutoChanged);
  }

  guestNameManuallyChanged() {
    this.sendToPubSub(EventName.GuestNameManuallyChanged);
  }

  customEvent(event: string, props: Record<string, unknown> = {}) {
    this.clientsiteSharedLoggingService.sendToPubSub([LogChannel.Amplitude], { name: event, props, type: null });
  }

  pwaDownloaded(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.PWADownloaded, props);
  }

  pwaDismissed(props: Record<string, unknown>) {
    this.sendToPubSub(EventName.PWADismissed, props);
  }

  noAvailabilityLocation() {
    this.sendToPubSub(EventName.NoAvailabilityLocation);
  }

  noAvailabilityHotel() {
    this.sendToPubSub(EventName.NoAvailabilityHotel);
  }

  locationTooLarge() {
    this.sendToPubSub(EventName.LocationTooLarge);
  }

  reportDateRange(report: string, datePickerChanges: DatePickerChanges) {
    const { dateType, startDate, endDate } = datePickerChanges;
    const props = {
      report,
      dateType,
      dates: {
        from: startDate,
        to: endDate,
      },
    };
    this.sendToPubSub(EventName.ReportDateRange, props);
  }

  updateLastBookingDate(userConfig: UserConfiguration, envConfig: EnvironmentConfiguration) {
    const { intercom } = envConfig.tracking;
    this.setUserProperties({
      app_id: intercom.key,
      company: {
        id: userConfig.general.websiteId,
        last_booking_at: getUnixTime(new Date()),
      },
    });
  }

  apartmentRequestSent() {
    this.sendToPubSub(EventName.ApartmentRequestSent);
  }

  landedOnBookingsPage() {
    this.sendToPubSub(EventName.LandedOnBookingsPage);
  }

  landedOnViewConfirmationPage() {
    this.sendToPubSub(EventName.LandedOnViewConfirmationPage);
  }

  landedOnHotelDetailsPage() {
    this.sendToPubSub(EventName.LandedHotelDetailsPage);
  }

  galleryImageClicked() {
    this.sendToPubSub(EventName.GalleryImageClicked);
  }

  clickedHotelOverview() {
    this.sendToPubSub(EventName.ClickedHotelOverview);
  }

  clickedBookARoom() {
    this.sendToPubSub(EventName.ClickedBookARoom);
  }

  clickedMapViewRoomSelect() {
    this.sendToPubSub(EventName.ClickedMapViewRoomSelect);
  }

  clickedHotelInformation() {
    this.sendToPubSub(EventName.ClickedHotelInformation);
  }

  clickedShareHotel() {
    this.sendToPubSub(EventName.ShareHotel);
  }

  searchBoxMealOptions(mealOption: string) {
    this.sendToPubSub(EventName.SelectedMoreOptions, { mealOption });
  }

  searchBoxPaymentOptions(paymentOption: string) {
    this.sendToPubSub(EventName.SelectedMoreOptions, { paymentOption });
  }

  amendDates() {
    this.sendToPubSub(EventName.AmendDates);
  }

  completeAutoDateChange() {
    this.sendToPubSub(EventName.CompleteAutoDateChange);
  }

  submitManualDateChange() {
    this.sendToPubSub(EventName.SubmitManualDateChange);
  }

  dateChangeNoAvailability() {
    this.sendToPubSub(EventName.DateChangeNoAvailability);
  }

  requestedRoomexPayCardAfterBooking() {
    this.sendToPubSub(EventName.RequestedRoomexPayCardAfterBooking);
  }

  addedRoomexPayCardAfterBooking() {
    this.sendToPubSub(EventName.AddedRoomexPayCardAfterBooking);
  }

  requestedRoomexPayInfo() {
    this.sendToPubSub(EventName.RoomexPayLead);
  }

  landedOnReportsTrainlinePage() {
    this.sendToPubSub(EventName.LandedOnReportsTrainlinePage);
  }

  landedOnReportsBookerPage() {
    this.sendToPubSub(EventName.LandedOnReportsBookerPage);
  }

  landedOnReportsHotelPage() {
    this.sendToPubSub(EventName.LandedOnReportsHotelPage);
  }

  landedOnReportsTravelerPage() {
    this.sendToPubSub(EventName.LandedOnReportsTravelerPage);
  }

  landedOnReportsCompliancePage() {
    this.sendToPubSub(EventName.LandedOnReportsCompliancePage);
  }

  landedOnReportsWorkforceTrackerPage() {
    this.sendToPubSub(EventName.LandedOnReportsWorkforceTrackerPage);
  }

  landedOnReportsRoomexPayPage() {
    this.sendToPubSub(EventName.LandedOnReportsRoomexPayPage);
  }

  landedOnReportsClientFieldPage() {
    this.sendToPubSub(EventName.LandedOnReportsClientFieldPage);
  }

  workforceTrackerCountry(country: string) {
    this.sendToPubSub(EventName.WorkforceTrackerCountry, { country });
  }

  selectedLocationFromAutoComplete(
    selectedLocation: AutocompleteLocationResponse,
    selectedLocationIndex: number,
    query: string,
    source?: string,
  ): void {
    this.sendToPubSub(EventName.SelectedLocationFromAutoComplete, {
      Source: source ?? (query?.length ? 'Suggested' : 'Preferred'),
      PromptLength: query?.length,
      Platform: selectedLocation.LocationCode.startsWith('GPv001-') ? 'Google Places' : 'Roomex',
      Index: selectedLocationIndex + 1,
    });
  }

  clickedOnRecentSearch(search: SearchForm): void {
    this.sendToPubSub(EventName.ClickedOnRecentSearch, {
      HotelSearch: !!search.hotelId,
      CheckinInPast: isPast(new Date(search.checkinDate)),
      NoRooms: search.rooms.filter((room) => room > 0).length,
      NoGuests: search.rooms.reduce((acc, val) => acc + val, 0),
    });
  }

  redirectToGroupBookingFrom8Rooms() {
    this.sendToPubSub(EventName.RedirectToGroupBookingFrom8Rooms);
  }

  openedLongStayRedirectModal() {
    this.sendToPubSub(EventName.OpenedLongStayRedirectModal);
  }

  redirectToGroupBookingFromLongStay() {
    this.sendToPubSub(EventName.RedirectToGroupBookingFromLongStay);
  }

  editLocationSearchClicked(): void {
    this.sendToPubSub(EventName.EditLocationSearchClicked);
  }

  mapHotelPinClicked(): void {
    this.sendToPubSub(EventName.MapHotelPinClicked);
  }

  selectHotelFromMapClicked(): void {
    this.sendToPubSub(EventName.SelectHotelFromMapClicked);
  }

  mapDragAndDrop(): void {
    this.sendToPubSub(EventName.MapDragAndDrop);
  }

  mapZoomWithMouse(): void {
    this.sendToPubSub(EventName.MapZoomWithMouse);
  }

  mapZoomWithButtons(): void {
    this.sendToPubSub(EventName.MapZoomWithButtons);
  }

  locationSearchClearAllFilters(): void {
    this.sendToPubSub(EventName.LocationSearchClearAllFilters);
  }

  getReservationDetailsSoldOut(action): void {
    this.sendToPubSub(EventName.GetReservationDetailsSoldOut);
    this.logEventToDatadog(EventName.GetReservationDetailsSoldOut, action);
  }

  private sendToPubSub(eventName: string, props: Record<string, unknown> = {}) {
    this.clientsiteSharedLoggingService.sendToPubSub(LogChannelMappings[eventName], {
      name: eventName,
      props,
      type: null,
    });
  }
}
